// src/context/useRecruitmentActions.js
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../backend/firebaseConfig";
import { useAdviserContext } from "../AdviserContext";

const TTL = 500 * 60 * 1000; // 10 min
const LS_KEY = (id) => `adviser:${id}`;

const readLS = (id) => {
  try {
    const raw = localStorage.getItem(LS_KEY(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (TTL && Date.now() - (parsed.savedAt || 0) > TTL) {
      // console.warn(`[RecruitmentActions] cache expired for ${id}`);
      return null;
    }
    // console.log(`[RecruitmentActions] cache hit for ${id}`, parsed);
    return parsed;
  } catch (err) {
    // console.error(`[RecruitmentActions] cache parse error`, err);
    return null;
  }
};

const writeLS = (id, data) => {
  // console.log(`[RecruitmentActions] writing cache for ${id}`, data);
  localStorage.setItem(
    LS_KEY(id),
    JSON.stringify({ savedAt: Date.now(), data })
  );
};

export function useAdviserActions() {
  const { state, dispatch } = useAdviserContext();

  const loadAdviser = async (
    docId,
    { forceRefresh = false, useLS = true } = {}
  ) => {
    // console.log("[RecruitmentActions] loadAdviser called:", {
    //   docId,
    //   forceRefresh,
    //   useLS,
    //   currentState: state,
    // });

    if (!forceRefresh && state.docId === docId && state.data) {
      // console.log("[RecruitmentActions] reusing existing state for", docId);
      return state.data;
    }

    dispatch({ type: "SET_LOADING", payload: true });

    try {
      // Try cache
      if (useLS && !forceRefresh) {
        const cached = readLS(docId);
        if (cached?.data) {
          // console.log("[RecruitmentActions] using cached data for", docId);
          dispatch({
            type: "SET_ADVISER_DATA",
            payload: { docId, data: cached.data },
          });
          return cached.data;
        }
      }

      // console.log("[RecruitmentActions] fetching from Firestore for", docId);

      // Firestore reads (parent + sections)
      const parentRef = doc(db, "Recruitment", docId);
      const refs = {
        personalDetails: doc(
          db,
          "Recruitment",
          docId,
          "Personal Details",
          "Data"
        ),
        applicationDetails: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Data"
        ),
        previousAddress: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Previous Addresses"
        ),
        nextOfKinDetails: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Next of Kin Details"
        ),
        professionalQualifications: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Professional Qualifications"
        ),
        lifeAssuranceCompanies: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Life Assurance Companies"
        ),
        workHistory: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Work History"
        ),
        companyDirectorships: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Company Directorships"
        ),
        accountantDetails: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Accountant Details"
        ),
        businessConduct: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Business Conduct"
        ),
        fitnessAndPropriety: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Fitness and Propriety"
        ),
        overallFinancialPosition: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Overall Financial Position"
        ),
        additionalInformation: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Additional Information"
        ),
        unemploymentDetails: doc(
          db,
          "Recruitment",
          docId,
          "Application Details",
          "Unemployment Details"
        ),
        agencyCode: doc(db, "Recruitment", docId, "Agency Codes", "Data"),
        ARDetails: doc(db, "Recruitment", docId, "AR Details", "Data")
      };

      const [parentSnap, ...sectionSnaps] = await Promise.all([
        getDoc(parentRef),
        ...Object.values(refs).map((r) => getDoc(r)),
      ]);

      // console.log(
      //   "[RecruitmentActions] parentSnap.exists:",
      //   parentSnap.exists()
      // );

      const baseData = parentSnap.exists() ? parentSnap.data() : null;
      const data = { baseData };
      Object.keys(refs).forEach((k, i) => {
        data[k] = sectionSnaps[i].exists() ? sectionSnaps[i].data() : null;
        // console.log(`[RecruitmentActions] section ${k}:`, data[k]);
      });

      // Single dispatch
      // console.log("[RecruitmentActions] dispatching SET_RECRUITMENT_DATA", {
      //   docId,
      //   data,
      // });
      dispatch({ type: "SET_ADVISER_DATA", payload: { docId, data } });

      if (useLS) writeLS(docId, data);

      return data;
    } catch (e) {
      // console.error("[RecruitmentActions] loadAdviser error", e);
      dispatch({ type: "SET_ERROR", payload: e?.message || "Failed to load" });
      return null;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
      // console.log("[RecruitmentActions] loadAdviser finished");
    }
  };

  const resetAdviser = () => {
    // console.log("[RecruitmentActions] resetAdviser called");
    dispatch({ type: "RESET" });
  };

  return { loadAdviser, resetAdviser };
}
